// Material Takeoffs · Project Workspace card.
//
// Read-only summary strip that surfaces the current takeoff state
// (None / Draft / Approved / Draft Revision) and links to the full
// editor at /app/projects/:pid/material-takeoff. Never mutates
// takeoff data — creation happens on the editor page so a contractor
// can pick the entry path deliberately.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Package, ChevronRight, ClipboardCheck, PencilLine } from "lucide-react";

const MaterialTakeoffCard = ({ projectId }) => {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get(`/projects/${projectId}/material-takeoffs`)
      .then((r) => { if (!cancelled) setRows(r.data || []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  // Highest-version = current draft or approved. Approved with a
  // newer draft child = "draft revision".
  const sorted = [...rows].sort((a, b) => (b.version || 0) - (a.version || 0));
  const current = sorted[0] || null;
  const stateKey =
    !current ? "none"
    : current.status === "approved" ? "approved"
    : current.parent_takeoff_id ? "draft_revision" : "draft";

  return (
    <section data-testid="material-takeoff-card" className="jc-card p-5 mb-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Package size={16} className="text-[var(--jc-text-soft)] shrink-0" />
          <div>
            <div className="overline">{t("material_takeoff.card_overline")}</div>
            <div className="text-sm font-semibold" data-testid="material-takeoff-state">
              {t(`material_takeoff.state.${stateKey}`)}
              {current ? ` · v${current.version}` : ""}
            </div>
            <div className="text-xs text-[var(--jc-text-soft)] mt-0.5">
              {loading ? t("material_takeoff.loading")
                : t(`material_takeoff.state_desc.${stateKey}`)}
            </div>
          </div>
        </div>
        <Link
          to={`/app/projects/${projectId}/material-takeoff`}
          data-testid="material-takeoff-open-btn"
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md jc-btn-primary"
        >
          {current
            ? (current.status === "approved"
                ? <><ClipboardCheck size={14} /> {t("material_takeoff.open_approved")}</>
                : <><PencilLine size={14} /> {t("material_takeoff.open_draft")}</>)
            : <>{t("material_takeoff.open_new")}<ChevronRight size={14} /></>}
        </Link>
      </div>
    </section>
  );
};

export default MaterialTakeoffCard;
